// Shared helper for surfacing the server's structured error to the user.
//
// Use this in EVERY mutation `onError` (and every `} catch (err)` that
// shows a toast) so teachers see the actual problem ("This code is
// locked to your school's email", "Subscribe to use AI polish") instead
// of a generic "Failed to save".
//
// Pattern:
//   onError: (err: unknown) => {
//     toast({
//       title: "Couldn't save the class",
//       description: getApiErrorMessage(err, "Try again."),
//       variant: "destructive",
//     });
//   }
//
// When the server returns a typed `errorCode`, prefer a switch on
// `getApiErrorBody(err)?.errorCode` so each code gets a tailored
// message + suggested next step (see `pages/app/settings.tsx` for a
// reference implementation). Fall back to `getApiErrorMessage(err, …)`
// for the default branch so the floor is always the server's free-text
// `error` string rather than the generic fallback.
//
// A static guard test (`api-error-helper.guard.test.ts`) fails CI if a
// new mutation/catch site uses `err.message` or a description-less
// destructive toast. Tag legitimate exceptions with
// `// api-error-helper-exempt: <reason>` immediately above the catch.
import { ApiError } from "@workspace/api-client-react";

export interface ApiErrorBody {
  errorCode?: string;
  error?: string;
  details?: unknown;
  /**
   * Boolean flags returned today by /ai/* routes (`subscriptionExpired`,
   * `upgradeRequired`). New routes should prefer `errorCode` instead,
   * but the existing AI polish flow still relies on these and the
   * tailored "Subscribe to use AI polish" copy depends on them firing.
   */
  subscriptionExpired?: boolean;
  upgradeRequired?: boolean;
}

export function getApiErrorBody(err: unknown): ApiErrorBody | null {
  // Accept either a thrown ApiError (the common mutation/onError path)
  // or a raw, already-parsed JSON body (the raw-fetch import dialogs
  // path) so both call sites can share the exact same extraction shape.
  let data: unknown = null;
  if (err instanceof ApiError) {
    data = err.data;
  } else if (err && typeof err === "object") {
    data = err;
  }
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  const hasErrorField =
    typeof obj.errorCode === "string" ||
    typeof obj.error === "string" ||
    obj.subscriptionExpired === true ||
    obj.upgradeRequired === true;
  if (!hasErrorField) return null;
  return {
    errorCode: typeof obj.errorCode === "string" ? obj.errorCode : undefined,
    error: typeof obj.error === "string" ? obj.error : undefined,
    details: obj.details,
    subscriptionExpired:
      obj.subscriptionExpired === true ? true : undefined,
    upgradeRequired: obj.upgradeRequired === true ? true : undefined,
  };
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  const body = getApiErrorBody(err);
  if (body?.error) return body.error;
  if (err instanceof ApiError) return err.message;
  return fallback;
}
